import { showAlert } from "@/utils/helpers";

export const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
export const MAX_IMAGE_SIZE_LABEL = "5 ميجابايت";
export const IMAGE_SIZE_ERROR_CODE = "RE_IMAGE_SIZE_LIMIT";
export const IMAGE_SIZE_ERROR_MESSAGE = `حجم الصورة يجب ألا يتجاوز ${MAX_IMAGE_SIZE_LABEL}`;

const IMAGE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "bmp",
  "webp",
]);

function getExtension(fileName = "") {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex >= 0 ? fileName.slice(dotIndex + 1).toLowerCase() : "";
}

export function isImageFile(file) {
  if (typeof File === "undefined" || !(file instanceof File)) return false;

  return (
    (file.type && file.type.toLowerCase().startsWith("image/")) ||
    IMAGE_EXTENSIONS.has(getExtension(file.name))
  );
}

function getBase64ImageSize(value) {
  if (typeof value !== "string") return null;

  const match = value.match(/^data:image\/[a-z0-9.+-]+;base64,([\s\S]*)$/i);
  if (!match) return null;

  const base64 = match[1].replace(/\s/g, "");
  if (!base64) return 0;

  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

export function findOversizedImage(value, visited = new WeakSet()) {
  if (value == null) return null;

  if (typeof File !== "undefined" && value instanceof File) {
    return isImageFile(value) && value.size > MAX_IMAGE_SIZE_BYTES
      ? { name: value.name || "الصورة", size: value.size }
      : null;
  }

  if (
    typeof Blob !== "undefined" &&
    value instanceof Blob &&
    value.type &&
    value.type.toLowerCase().startsWith("image/")
  ) {
    return value.size > MAX_IMAGE_SIZE_BYTES
      ? { name: "الصورة", size: value.size }
      : null;
  }

  if (typeof value === "string") {
    const size = getBase64ImageSize(value);
    return size !== null && size > MAX_IMAGE_SIZE_BYTES
      ? { name: "الصورة", size }
      : null;
  }

  if (typeof FormData !== "undefined" && value instanceof FormData) {
    for (const [, entryValue] of value.entries()) {
      const oversized = findOversizedImage(entryValue, visited);
      if (oversized) return oversized;
    }
    return null;
  }

  if (typeof value !== "object") return null;
  if (visited.has(value)) return null;
  visited.add(value);

  if (Array.isArray(value)) {
    for (const item of value) {
      const oversized = findOversizedImage(item, visited);
      if (oversized) return oversized;
    }
    return null;
  }

  for (const key of Object.keys(value)) {
    const oversized = findOversizedImage(value[key], visited);
    if (oversized) return oversized;
  }

  return null;
}

export function createImageSizeError(image) {
  const error = new Error(IMAGE_SIZE_ERROR_MESSAGE);
  error.code = IMAGE_SIZE_ERROR_CODE;
  error.response = {
    status: 400,
    data: {
      statusCode: IMAGE_SIZE_ERROR_CODE,
      message: IMAGE_SIZE_ERROR_MESSAGE,
      fileName: image && image.name ? image.name : null,
    },
  };
  return error;
}

export function installImageUploadGuard() {
  document.addEventListener(
    "change",
    (event) => {
      const input = event.target;

      if (
        !(input instanceof HTMLInputElement) ||
        input.type !== "file" ||
        !input.files
      ) {
        return;
      }

      const oversized = Array.from(input.files).find(
        (file) => isImageFile(file) && file.size > MAX_IMAGE_SIZE_BYTES
      );

      if (!oversized) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      input.value = "";

      showAlert(`${IMAGE_SIZE_ERROR_MESSAGE}: ${oversized.name}`);

      // Let Vue/Vuetify receive a second empty change event so any previous
      // v-model file value is cleared instead of remaining silently selected.
      window.setTimeout(() => {
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }, 0);
    },
    true
  );
}
